import { useState } from 'react';
import logger from '../../../utils/logger';
import CustomAlert from '../../../components/CustomAlert';
import { getLevelKey } from '../../../utils/getLevel';
import { buildProcessPath } from '../../../routes/buildProcessPath';
import {
  contectCancelPublishing,
  contentapproveProcess,
  contentreschedulePublishing,
  contentRequestChange,
  cancelPublishingAPI,
} from '../../../api/api';

export const useMapLevelApprovals = ({
  processId,
  currentLevel,
  currentParentId,
  LoginUser,
  title,
  t,
  refetchApprovalStatus,
}) => {
  const [editScheduledModalOpen, setEditScheduledModalOpen] = useState(false);
  const [requestChangeModalOpen, setRequestChangeModalOpen] = useState(false);

  const handleApproveProcess = async () => {
    CustomAlert.confirmAction({
      title: t('approve_process'),
      text: t('do_you_want_to_approve_the_process_and_inform_the_modeler_to_publish_it'),
      confirmBtnText: t('approve'),
      cancelBtnText: t('Cancel'),
      confirmCallback: async () => {
        const processPath = buildProcessPath({
          mode: 'draft',
          view: 'map',
          processId,
          level: currentLevel,
          parentId: currentLevel === 0 ? undefined : currentParentId,
        });

        const processLink = `${window.location.origin}${processPath}`;

        try {
          const payload = {
            process_id: processId,
            level: getLevelKey(currentLevel, currentParentId),
            user_id: LoginUser?.id,
            process_title: title,
            process_link: processLink,
          };
          const res = await contentapproveProcess(payload);
          if (res.status) {
            const msgT = t('process_approved_and_published_successfully');
            const event = new CustomEvent('modelSaved', { detail: { message: msgT } });
            window.dispatchEvent(event);

            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            CustomAlert.error('Error', res.message);
          }
        } catch (e) {
          logger.error(e);
          CustomAlert.error('Error', 'Failed to approve process');
        }
      },
    });
  };

  const handleCancelPublishing = async () => {
    CustomAlert.confirmAction({
      title: t('cancel_publishing'),
      text: t('do_you_really_want_to_cancel_publishing'),
      confirmBtnText: t('yes'),
      cancelBtnText: t('no'),
      confirmCallback: async () => {
        const processPath = buildProcessPath({
          mode: 'draft',
          view: 'map',
          processId,
          level: currentLevel,
          parentId: currentLevel === 0 ? undefined : currentParentId,
        });
        const processLink = `${window.location.origin}${processPath}`;

        try {
          const payload = {
            process_id: processId,
            level: getLevelKey(currentLevel, currentParentId),
            user_id: LoginUser?.id,
            process_title: title,
            process_link: processLink,
          };
          const res = await contectCancelPublishing(payload);
          if (res.status) {
            const msgT = t('publishing_cancelled');
            const event = new CustomEvent('modelSaved', { detail: { message: msgT } });
            window.dispatchEvent(event);

            setEditScheduledModalOpen(false);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            CustomAlert.error('Error', res.message);
          }
        } catch (e) {
          logger.error(e);
          CustomAlert.error('Error', 'Failed to cancel');
        }
      },
    });
  };

  const handleReschedulePublishing = async (newDate, timezone) => {
    const processPath = buildProcessPath({
      mode: 'draft',
      view: 'map',
      processId,
      level: currentLevel,
      parentId: currentLevel === 0 ? undefined : currentParentId,
    });
    const processLink = `${window.location.origin}${processPath}`;

    try {
      const payload = {
        process_id: processId,
        level: getLevelKey(currentLevel, currentParentId),
        user_id: LoginUser?.id,
        new_date: newDate,
        process_title: title,
        process_link: processLink,
        timezone: timezone,
      };
      const res = await contentreschedulePublishing(payload);
      if (res.status) {
        const msgT = t('publishing_rescheduled');
        const event = new CustomEvent('modelSaved', { detail: { message: msgT } });
        window.dispatchEvent(event);

        setEditScheduledModalOpen(false);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        CustomAlert.error('Error', res.message);
      }
    } catch (e) {
      logger.error(e);
      CustomAlert.error('Error', 'Failed to reschedule');
    }
  };

  const handleContentChangeRequest = async (reason) => {
    const processPath = buildProcessPath({
      mode: 'draft',
      view: 'map',
      processId,
      level: currentLevel,
      parentId: currentLevel === 0 ? undefined : currentParentId,
    });
    const processLink = `${window.location.origin}${processPath}`;
    try {
      const payload = {
        process_id: processId,
        level: getLevelKey(currentLevel, currentParentId),
        user_id: LoginUser?.id,
        reason: reason,
        process_title: title,
        process_link: processLink,
      };
      const res = await contentRequestChange(payload);
      if (res.status) {
        const msgT = t('change_request_sent_successfully');
        const event = new CustomEvent('modelSaved', { detail: { message: msgT } });
        window.dispatchEvent(event);

        setRequestChangeModalOpen(false);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        CustomAlert.error('Error', res.message);
      }
    } catch (e) {
      logger.error(e);
      CustomAlert.error('Error', 'Failed to request change');
    }
  };

  const handleCancelApproval = async () => {
    try {
      const levelKey = getLevelKey(currentLevel, currentParentId);
      await cancelPublishingAPI({
        process_id: processId,
        level: levelKey,
        user_id: LoginUser?.id,
        process_title: title,
        process_link: window.location.href,
      });
      const msgT = t('approval_cancelled_successfully');
      const event = new CustomEvent('modelSaved', { detail: { message: msgT } });
      window.dispatchEvent(event);

      if (refetchApprovalStatus) refetchApprovalStatus();
    } catch (error) {
      logger.error('Cancel approval error:', error);
      CustomAlert.error(t('error'), t('Failed to cancel approval'));
    }
  };

  return {
    editScheduledModalOpen,
    setEditScheduledModalOpen,
    requestChangeModalOpen,
    setRequestChangeModalOpen,
    handleApproveProcess,
    handleCancelPublishing,
    handleReschedulePublishing,
    handleContentChangeRequest,
    handleCancelApproval,
  };
};
